import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdQueryKey,
  useGetApiCompaniesCurrentServicemodulesStatussystemServiceModuleId,
  usePatchApiCompaniesCurrentServicemodulesStatussystemServiceModuleId,
  usePostApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevels,
  usePutApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevelsStatusLevelId,
  useDeleteApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevelsStatusLevelId,
} from '@/api/generated/company-management-user/company-management-user'
import {
  PermissionEntityType,
  type CreateStatusLevelModel,
  type ServiceModuleModel,
  type StatusLevelModel,
  type StatusSystemServiceModuleModel,
  type UpdateStatusSystemServiceModuleModel,
} from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { notify } from '@/lib/notify'
import { usePermissions } from '@/hooks/usePermissions'
import { StatusModuleSettingsCard } from './StatusModuleSettingsCard'
import { StatusLevelsTable } from './StatusLevelsTable'
import { StatusLevelDialog } from './StatusLevelDialog'
import { unwrap } from '@/utils/unwrap.ts'

interface Props {
  module: ServiceModuleModel
}

/**
 * Status-system service module as its own page (card click → `?open`). CRUD/
 * configuration UI for the module (schedule + wallets) and its level ladder.
 * The actual point accrual + the scheduled payout run in the backend.
 * After every mutation the module query is invalidated (fetch-after-mutate, no
 * optimistic updates) — among other things so the server-computed `nextExecution` appears.
 */
export function StatusServiceModulePage({ module }: Props) {
  const { t } = useTranslation(['service-modules-status', 'common'])
  const id = module.id
  const queryClient = useQueryClient()

  const { userHasEntityPermission } = usePermissions()
  const canEdit = userHasEntityPermission(PermissionEntityType.ServiceModules, 'Edit', id)

  const moduleQuery = useGetApiCompaniesCurrentServicemodulesStatussystemServiceModuleId(id)
  const detail = unwrap<StatusSystemServiceModuleModel>(moduleQuery.data)
  const levels = detail?.statusLevels ?? []

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingLevel, setEditingLevel] = useState<StatusLevelModel | undefined>()
  const [confirmDeleteLevel, setConfirmDeleteLevel] = useState<StatusLevelModel | undefined>()

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getGetApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdQueryKey(id),
    })

  const patchModule = usePatchApiCompaniesCurrentServicemodulesStatussystemServiceModuleId()
  const createLevel = usePostApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevels()
  const updateLevel = usePutApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevelsStatusLevelId()
  const deleteLevel = useDeleteApiCompaniesCurrentServicemodulesStatussystemServiceModuleIdLevelsStatusLevelId()

  const handleSaveSettings = (data: UpdateStatusSystemServiceModuleModel) => {
    patchModule.mutate(
      { serviceModuleId: id, data },
      {
        onSuccess: () => {
          notify.success(t('toasts.moduleUpdated'))
          invalidate()
        },
        onError: (error) => notify.apiError(error, t('toasts.moduleUpdateError')),
      },
    )
  }

  const handleSaveLevel = async (
    data: CreateStatusLevelModel,
    levelId: string | undefined,
    onSuccess?: (savedId: string) => Promise<void>,
  ) => {
    if (levelId) {
      updateLevel.mutate(
        { serviceModuleId: id, statusLevelId: levelId, data },
        {
          onSuccess: async () => {
            await onSuccess?.(levelId)
            notify.success(t('toasts.levelUpdated'))
            setEditingLevel(undefined)
            invalidate()
          },
          onError: (error) => notify.apiError(error, t('toasts.levelUpdateError')),
        },
      )
    } else {
      createLevel.mutate(
        { serviceModuleId: id, data },
        {
          onSuccess: async (response) => {
            const savedId = unwrap<StatusLevelModel>(response)?.id
            if (savedId) await onSuccess?.(savedId)
            notify.success(t('toasts.levelCreated'))
            setIsCreateOpen(false)
            invalidate()
          },
          onError: (error) => notify.apiError(error, t('toasts.levelCreateError')),
        },
      )
    }
  }

  const handleDeleteLevel = () => {
    if (!confirmDeleteLevel) return
    deleteLevel.mutate(
      { serviceModuleId: id, statusLevelId: confirmDeleteLevel.id },
      {
        onSuccess: () => {
          notify.success(t('toasts.levelDeleted'))
          setConfirmDeleteLevel(undefined)
          invalidate()
        },
        onError: (error) => notify.apiError(error, t('toasts.levelDeleteError')),
      },
    )
  }

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={module.name || t('title')} description={t('description')} />

      {detail && (
        <>
          <StatusModuleSettingsCard
            module={detail}
            canEdit={canEdit}
            isSaving={patchModule.isPending}
            onSave={handleSaveSettings}
          />

          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">{t('levels.title')}</h2>
            {canEdit && (
              <Button onClick={() => setIsCreateOpen(true)} className="gap-2">
                <Plus className="size-4" />
                {t('levels.createButton')}
              </Button>
            )}
          </div>

          <StatusLevelsTable
            levels={levels}
            canEdit={canEdit}
            onEdit={setEditingLevel}
            onDelete={setConfirmDeleteLevel}
          />
        </>
      )}

      {isCreateOpen && (
        <StatusLevelDialog
          onClose={() => setIsCreateOpen(false)}
          onSave={handleSaveLevel}
          isSaving={createLevel.isPending}
          serviceModuleId={id}
          siblings={levels}
        />
      )}
      {editingLevel && (
        <StatusLevelDialog
          onClose={() => setEditingLevel(undefined)}
          onSave={handleSaveLevel}
          isSaving={updateLevel.isPending}
          serviceModuleId={id}
          level={editingLevel}
          siblings={levels.filter((l) => l.id !== editingLevel.id)}
        />
      )}

      <ConfirmDialog
        open={!!confirmDeleteLevel}
        title={t('levels.deleteTitle')}
        description={t('levels.deleteDescription', { name: confirmDeleteLevel?.name })}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={deleteLevel.isPending}
        onConfirm={handleDeleteLevel}
        onClose={() => setConfirmDeleteLevel(undefined)}
      />
    </PageLayout>
  )
}
